import { CorpusId, WidgetId } from '../../models/aliases';
import { PaperReaction, PaperReactions } from '../../models/reactions';

import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { Draft } from '@reduxjs/toolkit';

// ------------- //
// Reactions API //
// ------------- //

export interface LoadReactionsParams {
  baseUrl: string;
  widgetId: WidgetId;
}

export interface UpdateReactionParams {
  baseUrl: string;
  widgetId: WidgetId;
  reaction: PaperReaction;
}

export interface SubmitFeedbackParams {
  baseUrl: string;
  widgetId: WidgetId;
  corpusId: CorpusId;
  text: string;
}

const MAX_RETRIES = 5;
export const paperFinderReactionsApi = createApi({
  reducerPath: 'paperFinder/reactions',
  baseQuery: retry(fetchBaseQuery({ baseUrl: '' }), {
    maxRetries: MAX_RETRIES,
  }),
  endpoints: (build) => ({
    reactions: build.query<PaperReactions, LoadReactionsParams>({
      query: ({ baseUrl, widgetId: roundId }) => ({
        url: `${baseUrl}rounds/${encodeURI(roundId)}/reactions`,
      }),
      // NOTE: server side treats corpus id as string, we do the conversion
      // as close as possible to the api call
      transformResponse: (response: any) => {
        return {
          reactions: response.reactions.map((r: any) => ({
            reaction: r.reaction,
            corpusId: parseInt(r.corpus_id),
          })),
        };
      },
    }),
    updateReaction: build.mutation<void, UpdateReactionParams>({
      query: ({ baseUrl, widgetId: roundId, reaction }) => ({
        url: `${baseUrl}rounds/${encodeURI(roundId)}/reactions`,
        method: 'POST',
        // NOTE: server side treats corpus id as string, we do the conversion
        // as close as possible to the api call
        body: {
          reaction: reaction.reaction,
          corpus_id: reaction.corpusId.toString(),
        },
      }),
      // The concept was taken from this example:
      //  https://redux.js.org/tutorials/essentials/part-8-rtk-query-advanced#implementing-optimistic-updates
      async onQueryStarted(
        { baseUrl, widgetId, reaction: update },
        lifecycleApi,
      ) {
        // update the "reactions" endpoint cache directly
        const getReactionsPatch = lifecycleApi.dispatch(
          paperFinderReactionsApi.util.updateQueryData(
            'reactions',
            { baseUrl, widgetId },
            // perform local state update
            (stateDraft) => {
              updateState(stateDraft, update);
            },
          ),
        );

        try {
          await lifecycleApi.queryFulfilled;
        } catch {
          getReactionsPatch.undo();
        }
      },
    }),
    submitTextFeedback: build.mutation<void, SubmitFeedbackParams>({
      query: ({ baseUrl, widgetId: roundId, corpusId, text }) => ({
        url: `${baseUrl}rounds/${encodeURI(roundId)}/feedback`,
        method: 'POST',
        // NOTE: server side treats corpus id as string, we do the conversion
        // as close as possible to the api call
        body: {
          text,
          corpus_id: corpusId.toString(),
        },
      }),
    }),
  }),
});

function updateState(
  state: Draft<PaperReactions>,
  update: PaperReaction,
): void {
  const reactions = state.reactions;
  const reactionIndex = reactions.findIndex(
    (r) => r.corpusId === update.corpusId,
  );
  const reaction = reactionIndex >= 0 ? reactions[reactionIndex] : undefined;

  if (!reaction) {
    // no matching entry exists, add as new entry
    reactions.push(update);
  } else {
    if (update.reaction !== undefined) {
      // reaction exists on update, update current entry
      reaction.reaction = update.reaction;
    } else {
      // No reaction exists on update, delete existing entry
      reactions.splice(reactionIndex, 1);
    }
  }
}

export const {
  useReactionsQuery,
  useUpdateReactionMutation,
  useSubmitTextFeedbackMutation,
} = paperFinderReactionsApi;
